import { createContext, useContext, useMemo, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";

/**
 * Feature flags exposed from the backend via Team API
 * These must match FRONTEND_FEATURE_FLAGS in apps/teams/serializers.py
 */
export const FEATURE_FLAGS = {
  HEALTH_SCORE_V2: "health_score_v2",
  AI_INSIGHTS: "ai_insights",
  PRODUCT_USAGE_INTEGRATION: "product_usage_integration",
  ACTIONS_V1: "actions_v1",
  ACCOUNT_STATES: "account_states",
  PILOT_PIPELINE_SUPPORT: "pilot_pipeline_support",
  SLACK_CHANNEL_LINKING: "slack_channel_linking",
  SIGNAL_ACTION_PLAYBOOKS: "signal_action_playbooks",
  ESTIMATED_GOAL_PILL: "estimated_goal_pill",
  RENEWAL_GOAL_FROZEN: "renewal_goal_frozen",
  HUBSPOT_SIGNAL_WRITEBACK: "hubspot_signal_writeback",
};

// Default values when flags aren't loaded yet
const DEFAULT_FLAGS = {
  [FEATURE_FLAGS.HEALTH_SCORE_V2]: false,
  [FEATURE_FLAGS.AI_INSIGHTS]: false,
  [FEATURE_FLAGS.PRODUCT_USAGE_INTEGRATION]: false,
  [FEATURE_FLAGS.ACTIONS_V1]: false,
  [FEATURE_FLAGS.ACCOUNT_STATES]: false,
  [FEATURE_FLAGS.PILOT_PIPELINE_SUPPORT]: false,
  [FEATURE_FLAGS.SLACK_CHANNEL_LINKING]: false,
  [FEATURE_FLAGS.SIGNAL_ACTION_PLAYBOOKS]: false,
  [FEATURE_FLAGS.ESTIMATED_GOAL_PILL]: false,
  [FEATURE_FLAGS.RENEWAL_GOAL_FROZEN]: false,
  [FEATURE_FLAGS.HUBSPOT_SIGNAL_WRITEBACK]: false,
};

export const FeatureFlagsContext = createContext({
  flags: DEFAULT_FLAGS,
  isEnabled: () => false,
  isLoading: true,
});

export const FeatureFlagsProvider = ({ children }) => {
  const { team, teamId, fetchTeamDetails } = useAuth();

  // Fetch team details when teamId is available (for feature flags)
  useEffect(() => {
    if (teamId && !team) {
      fetchTeamDetails();
    }
  }, [teamId, team, fetchTeamDetails]);

  /* eslint-disable react-hooks/exhaustive-deps */
  const flags = useMemo(() => {
    if (!team?.feature_flags) {
      return DEFAULT_FLAGS;
    }
    return {
      ...DEFAULT_FLAGS,
      ...team.feature_flags,
    };
  }, [team?.feature_flags]);

  const isEnabled = (flagName) => {
    return flags[flagName] || false;
  };

  const isLoading = !team;

  const value = useMemo(
    () => ({
      flags,
      isEnabled,
      isLoading,
    }),
    [flags, isLoading],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Hook to access feature flags
 * @returns {{ flags: object, isEnabled: (flagName: string) => boolean, isLoading: boolean }}
 */
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within FeatureFlagsProvider");
  }
  return context;
};

/**
 * Hook to check a single feature flag
 * @param {string} flagName - The name of the flag to check
 * @returns {boolean} Whether the flag is enabled
 */
export const useFeatureFlag = (flagName) => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flagName);
};
